import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Funnel, Stack } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const RESOURCE_TYPES = [
  { value: "purchase_order", label: "Purchase Order", amount_field: "total" },
  { value: "leave",          label: "Leave Request",  amount_field: null,  extra_fields: ["department"] },
  { value: "payroll_run",    label: "Payroll Run",    amount_field: "total" },
  { value: "permit",         label: "HSE Permit",     amount_field: null,  extra_fields: ["work_type", "risk_level"] },
  { value: "work_order",     label: "EAM Work Order", amount_field: null,  extra_fields: ["priority"] },
  { value: "ap_invoice",     label: "AP Invoice",     amount_field: "amount" },
];
const OPS = [
  { value: "gt",  label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt",  label: "<" },
  { value: "lte", label: "≤" },
  { value: "eq",  label: "=" },
  { value: "neq", label: "≠" },
];
const newClientId = () => `c-${Math.random().toString(36).slice(2, 9)}`;
const newCondition = (field) => ({ _cid: newClientId(), field: field || "status", op: "gt", value: "" });
const newStep = (idx) => ({ _cid: newClientId(), name: `Step ${idx}`, approver_ids: [] });
const EMPTY_TEMPLATE = () => ({ name: "", resource_type: "purchase_order", conditions: [], steps: [{ _cid: newClientId(), name: "Approver", approver_ids: [] }], active: true });

function ConditionRow({ value, fields, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <select value={value.field} onChange={(e) => onChange({ ...value, field: e.target.value })} className="border-b border-zinc-300 bg-transparent py-1 outline-none">
        {fields.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <select value={value.op} onChange={(e) => onChange({ ...value, op: e.target.value })} className="border-b border-zinc-300 bg-transparent py-1 outline-none">
        {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <input value={value.value ?? ""} onChange={(e) => onChange({ ...value, value: e.target.value })} placeholder="value" className="border-b border-zinc-300 bg-transparent py-1 outline-none flex-1 font-mono" />
      <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-[#D0021B]" data-testid="condition-remove"><Trash size={12} /></button>
    </div>
  );
}

function StepRow({ step, users, onChange, onRemove, canRemove }) {
  const toggle = (uid) => {
    const set = new Set(step.approver_ids);
    if (set.has(uid)) set.delete(uid); else set.add(uid);
    onChange({ ...step, approver_ids: [...set] });
  };
  return (
    <div className="border border-zinc-200 p-3 space-y-2 bg-zinc-50">
      <div className="flex items-center gap-2">
        <input value={step.name} onChange={(e) => onChange({ ...step, name: e.target.value })} placeholder="Step name" className="border-b border-zinc-300 bg-transparent py-1 text-sm outline-none flex-1" />
        {canRemove && <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-[#D0021B]"><Trash size={12} /></button>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {users.map((u) => {
          const on = step.approver_ids.includes(u.id);
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => toggle(u.id)}
              className={`text-[10px] font-mono px-2 py-1 border transition-colors ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-950"}`}
              data-testid={`step-approver-${u.id}`}
            >
              {u.email}
            </button>
          );
        })}
      </div>
      {!step.approver_ids.length && <div className="text-[10px] text-[#D0021B] font-mono">SELECT AT LEAST ONE APPROVER</div>}
    </div>
  );
}

export default function Workflows() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState(EMPTY_TEMPLATE());

  const refresh = useCallback(async () => {
    try {
      const [t, u] = await Promise.all([
        api.get("/workflows/templates").then((r) => r.data),
        api.get("/tenant/users").then((r) => r.data),
      ]);
      setTemplates(t); setUsers(u);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Workflows load failed", err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const meta = RESOURCE_TYPES.find((r) => r.value === draft.resource_type) || RESOURCE_TYPES[0];
  const availableFields = [
    ...(meta.amount_field ? [meta.amount_field] : []),
    ...(meta.extra_fields || []),
  ];

  const save = async (e) => {
    e.preventDefault();
    if (!draft.name || !draft.steps.length || draft.steps.some((s) => !s.approver_ids.length)) {
      toast.error("Name + at least one step with approvers required");
      return;
    }
    // Strip client-only `_cid` field, coerce numeric values for amount-like fields
    const conditions = draft.conditions.map(({ _cid, ...c }) => {
      const num = parseFloat(c.value);
      return { ...c, value: !isNaN(num) && /^[\d.\-]+$/.test(c.value) ? num : c.value };
    });
    const steps = draft.steps.map(({ _cid, ...s }) => s);
    try {
      await api.post("/workflows/templates", { ...draft, conditions, steps });
      toast.success("Workflow template saved");
      setDraft(EMPTY_TEMPLATE());
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this workflow template?")) return;
    await api.delete(`/workflows/templates/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const updateStep = (idx, value) => setDraft({ ...draft, steps: draft.steps.map((s, i) => i === idx ? value : s) });

  if (user?.role !== "admin") {
    return (
      <div className="p-6 sm:p-8" data-testid="workflows-page">
        <h1 className="font-display text-3xl font-black tracking-tighter">Workflows.</h1>
        <div className="mt-6 border border-zinc-200 p-6 text-sm text-zinc-500">Only admins can manage workflow templates. <a href="/approvals" className="text-[#FF3E00] hover:underline">View your approvals inbox →</a></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="workflows-page">
      <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 11 · WORKFLOWS</div>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Workflows.</h1>
      <p className="text-sm text-zinc-500 mt-2 max-w-xl">Define conditional approval chains across Finance, HR, SCM, HSE and EAM. Triggered automatically on record creation.</p>

      <section className="border border-zinc-950 mt-6">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
          <Plus size={12} /> new template
        </div>
        <form onSubmit={save} className="p-5 space-y-4" data-testid="workflow-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-sm text-zinc-500">Template name</label>
              <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" data-testid="workflow-name-input" />
            </div>
            <div>
              <label className="label-sm text-zinc-500">Resource type</label>
              <select value={draft.resource_type} onChange={(e) => setDraft({ ...draft, resource_type: e.target.value, conditions: [] })} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" data-testid="workflow-resource-select">
                {RESOURCE_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="label-sm text-zinc-500 mb-2 flex items-center gap-2"><Funnel size={11} /> Conditions (ALL must match — leave empty to trigger on every record)</div>
            <div className="space-y-2">
              {draft.conditions.map((c, i) => (
                <ConditionRow
                  key={c._cid}
                  value={c}
                  fields={availableFields.length ? availableFields : ["status"]}
                  onChange={(v) => setDraft({ ...draft, conditions: draft.conditions.map((x, j) => j === i ? { ...v, _cid: c._cid } : x) })}
                  onRemove={() => setDraft({ ...draft, conditions: draft.conditions.filter((_, j) => j !== i) })}
                />
              ))}
              <button type="button" onClick={() => setDraft({ ...draft, conditions: [...draft.conditions, newCondition(availableFields[0])] })} className="text-xs font-mono text-zinc-500 hover:text-zinc-950" data-testid="add-condition-btn">+ ADD CONDITION</button>
            </div>
          </div>

          <div>
            <div className="label-sm text-zinc-500 mb-2 flex items-center gap-2"><Stack size={11} /> Approval steps (sequential)</div>
            <div className="space-y-2">
              {draft.steps.map((s, i) => (
                <StepRow
                  key={s._cid}
                  step={s}
                  users={users}
                  onChange={(v) => updateStep(i, { ...v, _cid: s._cid })}
                  onRemove={() => setDraft({ ...draft, steps: draft.steps.filter((_, j) => j !== i) })}
                  canRemove={draft.steps.length > 1}
                />
              ))}
              <button type="button" onClick={() => setDraft({ ...draft, steps: [...draft.steps, newStep(draft.steps.length + 1)] })} className="text-xs font-mono text-zinc-500 hover:text-zinc-950" data-testid="add-step-btn">+ ADD STEP</button>
            </div>
          </div>

          <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-6 py-2.5 hover:bg-[#FF3E00]" data-testid="workflow-save-btn">SAVE TEMPLATE</button>
        </form>
      </section>

      <section className="mt-8">
        <div className="label-sm text-zinc-500 mb-3">Existing templates ({templates.length})</div>
        <div className="space-y-2" data-testid="workflow-templates-list">
          {templates.map((t) => (
            <div key={t.id} className="border border-zinc-200 p-4 flex items-start justify-between hover:border-zinc-950 transition-colors" data-testid={`template-${t.id}`}>
              <div>
                <div className="font-semibold">{t.name}</div>
                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                  {t.resource_type.toUpperCase()} · {t.conditions.length ? t.conditions.map((c) => `${c.field} ${OPS.find((o) => o.value === c.op)?.label} ${c.value}`).join(" AND ") : "always trigger"}
                </div>
                <div className="font-mono text-[10px] text-zinc-400 mt-1">{t.steps.length} step(s) · {t.steps.map((s) => s.name).join(" → ")}</div>
              </div>
              <button onClick={() => remove(t.id)} className="text-zinc-400 hover:text-[#D0021B]" data-testid={`template-delete-${t.id}`}><Trash size={14} /></button>
            </div>
          ))}
          {!templates.length && <div className="text-xs text-zinc-400 font-mono">NO TEMPLATES YET</div>}
        </div>
      </section>
    </div>
  );
}
